import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PromoConfig = {
  enabled: boolean;
  bannerText: string;
  ctaLabel: string;
  ctaHref: string;
};

export const DEFAULT_PROMO_CONFIG: PromoConfig = {
  enabled: true,
  bannerText: "Limited-time offer available.",
  ctaLabel: "See pricing",
  ctaHref: "/portal/sign-up",
};

const PROMO_STORAGE_KEY = "shadowscan_promo_config";

const sanitizeString = (value: unknown, fallback: string) => {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const normalizePromoConfig = (raw: Partial<PromoConfig>): PromoConfig => ({
  enabled: raw.enabled ?? DEFAULT_PROMO_CONFIG.enabled,
  bannerText: sanitizeString(raw.bannerText, DEFAULT_PROMO_CONFIG.bannerText),
  ctaLabel: sanitizeString(raw.ctaLabel, DEFAULT_PROMO_CONFIG.ctaLabel),
  ctaHref: sanitizeString(raw.ctaHref, DEFAULT_PROMO_CONFIG.ctaHref),
});

const readPromoConfig = (): PromoConfig => {
  if (typeof window === "undefined") return DEFAULT_PROMO_CONFIG;
  try {
    const stored = window.localStorage.getItem(PROMO_STORAGE_KEY);
    if (!stored) return DEFAULT_PROMO_CONFIG;
    const parsed = JSON.parse(stored) as Partial<PromoConfig>;
    return normalizePromoConfig(parsed || {});
  } catch {
    return DEFAULT_PROMO_CONFIG;
  }
};

const writePromoConfig = (config: PromoConfig) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage errors.
  }
};

const toPromoConfig = (row: {
  enabled: boolean;
  banner_text: string;
  cta_href: string;
  cta_label: string;
}): PromoConfig => normalizePromoConfig({
  enabled: row.enabled,
  bannerText: row.banner_text,
  ctaHref: row.cta_href,
  ctaLabel: row.cta_label,
});

export const usePromoConfig = () => {
  const [config, setConfig] = useState<PromoConfig>(() => readPromoConfig());

  const fetchPromoConfig = async () => {
    const { data, error } = await supabase
      .from("promo_settings")
      .select("enabled, banner_text, cta_label, cta_href")
      .maybeSingle();
    if (error || !data) return;
    const normalized = toPromoConfig(data);
    setConfig(normalized);
    writePromoConfig(normalized);
  };

  useEffect(() => {
    void fetchPromoConfig();
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== PROMO_STORAGE_KEY) return;
      setConfig(readPromoConfig());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const updateConfig = async (next: PromoConfig) => {
    const normalized = normalizePromoConfig(next);
    writePromoConfig(normalized);
    setConfig(normalized);

    const { error } = await supabase
      .from("promo_settings")
      .update({
        enabled: normalized.enabled,
        banner_text: normalized.bannerText,
        cta_label: normalized.ctaLabel,
        cta_href: normalized.ctaHref,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1);
    if (error) {
      console.warn("Promo settings update failed", error.message);
      void fetchPromoConfig();
    }
  };

  const resetConfig = () => updateConfig(DEFAULT_PROMO_CONFIG);

  return { config, updateConfig, resetConfig };
};
